import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, TrendingUp, Users, MessageCircle, Brain, Sparkles, Star, Smile, HeartHandshake, UsersRound, Leaf, CloudRain, Waves, Cloud, Sun } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [moodInsights, setMoodInsights] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [supportGroups, setSupportGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [showMoodOptions, setShowMoodOptions] = useState(false);

  // Function to get mood trend icon and styling based on trend value
  const getMoodTrendStyle = (trend) => {
    const trendLower = trend?.toLowerCase() || 'stable';
    
    switch (trendLower) {
      case 'improving':
      case 'positive':
      case 'upward':
        return {
          icon: Star,
          bgGradient: 'from-green-100 to-emerald-200 dark:from-green-800 dark:to-emerald-700',
          iconColor: 'text-emerald-600 dark:text-emerald-400',
          description: 'Improving'
        };
      case 'declining':
      case 'negative':
      case 'downward':
        return {
          icon: CloudRain,
          bgGradient: 'from-red-100 to-rose-200 dark:from-red-800 dark:to-rose-700',
          iconColor: 'text-red-600 dark:text-red-400',
          description: 'Needs Attention'
        };
      case 'fluctuating':
      case 'variable':
      case 'mixed':
        return {
          icon: Waves,
          bgGradient: 'from-purple-100 to-violet-200 dark:from-purple-800 dark:to-violet-700',
          iconColor: 'text-purple-600 dark:text-purple-400',
          description: 'Variable'
        };
      case 'excellent':
      case 'great':
        return {
          icon: Sparkles,
          bgGradient: 'from-green-100 to-lime-200 dark:from-green-800 dark:to-lime-700',
          iconColor: 'text-lime-600 dark:text-lime-400',
          description: 'Excellent'
        };
      case 'stable':
      case 'steady':
      default:
        return {
          icon: Cloud,
          bgGradient: 'from-blue-100 to-sky-200 dark:from-blue-800 dark:to-sky-700',
          iconColor: 'text-blue-600 dark:text-blue-400',
          description: 'Stable'
        };
    }
  };

  useEffect(() => {
    // Load favorites count from localStorage
    const loadFavoritesCount = () => {
      const savedFavorites = localStorage.getItem('favoritedMatches');
      if (savedFavorites) {
        try {
          const favorites = JSON.parse(savedFavorites);
          setFavoritesCount(favorites.length);
        } catch (e) {
          console.error('Error loading favorites count:', e);
        }
      }
    };

    fetchDashboardData();
    loadFavoritesCount();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [moodRes, matchesRes, groupsRes] = await Promise.all([
        axios.get('/mood/insights'),
        axios.get('/matches'),
        axios.get('/groups')
      ]);
      
      setMoodInsights(moodRes.data);
      setRecentMatches(matchesRes.data.slice(0, 3));
      setSupportGroups(groupsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-6 pb-12" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="card-gradient animate-fade-in-up">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-emerald-800 mb-2">
                  Welcome back, {user.full_name}!
                </h1>
                <p className="text-emerald-600 text-lg">
                  How are you feeling today? Your wellness journey continues here.
                </p>
              </div>
              <div className="hidden md:block">
                <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-200 dark:from-emerald-800 dark:to-teal-700 rounded-2xl flex items-center justify-center">
                  <Leaf className="w-12 h-12 text-emerald-600 dark:text-emerald-300" strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="relative">
            <div 
              onClick={() => setShowMoodOptions(!showMoodOptions)}
              className="card text-center animate-fade-in-up stagger-1 hover:scale-[1.02] transition-all duration-200 cursor-pointer" 
              data-testid="mood-score-card"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-200 dark:from-purple-800 dark:to-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Brain className="w-10 h-10 text-purple-600 dark:text-purple-300" />
              </div>
              <h3 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">
                {moodInsights?.average_mood || 'N/A'}
              </h3>
              <p className="text-emerald-600 dark:text-emerald-300 text-sm">Wellness Options</p>
            </div>

            {/* Dropdown Menu */}
            {showMoodOptions && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-emerald-200 dark:border-slate-600 z-50 overflow-hidden animate-fade-in">
                <div className="py-2">
                  <button
                    onClick={() => {
                      navigate('/mood');
                      setShowMoodOptions(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-3"
                  >
                    <span className="text-2xl">😊</span>
                    <div>
                      <p className="font-medium text-emerald-800 dark:text-emerald-100">Mood Tracker</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-300">Track your daily mood</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      navigate('/wellness');
                      setShowMoodOptions(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-3"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
                      <Leaf className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-800 dark:text-emerald-100">Wellness Resources</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-300">Mental health resources</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      navigate('/relax');
                      setShowMoodOptions(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-3"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-800 dark:text-emerald-100">Relax Games</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-300">Mindful relaxation</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      navigate('/groups');
                      setShowMoodOptions(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-3"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center">
                      <UsersRound className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-800 dark:text-emerald-100">Support Groups</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-300">Community support</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Click outside to close */}
            {showMoodOptions && (
              <div 
                className="fixed inset-0 z-40"
                onClick={() => setShowMoodOptions(false)}
              />
            )}
          </div>
          
          <Link to="/matches" className="block">
            <div className="card text-center animate-fade-in-up stagger-2 hover:scale-[1.02] transition-all duration-200 cursor-pointer" data-testid="matches-card">
              <div className="w-20 h-20 bg-gradient-to-br from-rose-100 to-pink-200 dark:from-rose-800 dark:to-pink-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Heart className="w-10 h-10 text-pink-600 dark:text-pink-300" strokeWidth={2} />
              </div>
              <h3 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">{recentMatches.length}</h3>
              <p className="text-emerald-600 dark:text-emerald-300 text-sm">Active Matches</p>
            </div>
          </Link>
          
          <div 
            onClick={() => navigate('/matches', { state: { defaultTab: 'favorites' } })}
            className="block cursor-pointer"
          >
            <div className="card text-center animate-fade-in-up stagger-3 hover:scale-[1.02] transition-all duration-200" data-testid="favorites-card">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-100 to-amber-200 dark:from-yellow-800 dark:to-amber-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Star className="w-10 h-10 text-yellow-600 dark:text-yellow-300 fill-current" />
              </div>
              <h3 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">{favoritesCount}</h3>
              <p className="text-emerald-600 dark:text-emerald-300 text-sm">Favorites</p>
            </div>
          </div>
          
          <Link to="/groups" className="block">
            <div className="card text-center animate-fade-in-up stagger-4 hover:scale-[1.02] transition-all duration-200 cursor-pointer" data-testid="groups-card">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-800 dark:to-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <UsersRound className="w-10 h-10 text-indigo-600 dark:text-indigo-300" strokeWidth={2} />
              </div>
              <h3 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">{supportGroups.length}</h3>
              <p className="text-emerald-600 dark:text-emerald-300 text-sm">Support Groups</p>
            </div>
          </Link>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Mood Insights */}
          <div className="lg:col-span-2">
            <div className="card animate-fade-in-up">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-emerald-800">Your Wellness Insights</h2>
                <Link to="/mood" className="text-emerald-600 hover:text-emerald-700 font-medium">
                  View Details →
                </Link>
              </div>
              
              {moodInsights ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4">
                    <h3 className="font-semibold text-emerald-800 mb-2">AI-Powered Suggestion</h3>
                    <p className="text-emerald-700">{moodInsights.suggestion}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-emerald-50 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-800">{moodInsights.total_entries}</div>
                      <div className="text-sm text-emerald-600">Entries This Week</div>
                    </div>
                    <div className="text-center p-4 bg-teal-50 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-800 capitalize">{moodInsights.trend}</div>
                      <div className="text-sm text-emerald-600">Overall Trend</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-200 dark:from-purple-800 dark:to-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Brain className="w-10 h-10 text-purple-600 dark:text-purple-300" strokeWidth={2} />
                  </div>
                  <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-2">Start Your Mood Journey</h3>
                  <p className="text-emerald-600 dark:text-emerald-300 mb-4">Track your daily mood to get personalised insights!</p>
                  <Link to="/mood" className="btn-primary inline-flex items-center space-x-2">
                    <Sparkles className="w-4 h-4" strokeWidth={2} />
                    <span>Track Mood</span>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            {/* Recent Matches */}
            <div className="card animate-fade-in-up stagger-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-emerald-800">Recent Matches</h3>
                <Link to="/matches" className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
                  View All →
                </Link>
              </div>
              
              {recentMatches.length > 0 ? (
                <div className="space-y-3">
                  {recentMatches.map((match, index) => (
                    <Link key={match.id} to="/matches" className="block">
                      <div className="flex items-center space-x-3 p-3 bg-emerald-50 dark:bg-slate-800 rounded-lg hover:bg-emerald-100 dark:hover:bg-slate-700 transition-colors cursor-pointer">
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-rose-200 dark:from-pink-800 dark:to-rose-700 rounded-xl flex items-center justify-center">
                          <Heart className="w-6 h-6 text-pink-600 dark:text-pink-300" strokeWidth={2} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-100">New Match</p>
                          <p className="text-xs text-emerald-600 dark:text-emerald-300">{Math.round(match.compatibility_score)}% Compatible</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <Link to="/matches" className="block">
                  <div className="text-center py-6 hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer">
                    <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-rose-200 dark:from-pink-800 dark:to-rose-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Heart className="w-8 h-8 text-pink-600 dark:text-pink-300" strokeWidth={2} />
                    </div>
                    <p className="text-sm text-emerald-600 dark:text-emerald-300">No matches yet. Start browsing!</p>
                  </div>
                </Link>
              )}
            </div>

            {/* Support Groups */}
            <div className="card animate-fade-in-up stagger-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-emerald-800">Support Groups</h3>
                <Link to="/groups" className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
                  View All →
                </Link>
              </div>
              
              {supportGroups.length > 0 ? (
                <div className="space-y-3">
                  {supportGroups.map((group, index) => (
                    <Link key={group.id} to="/groups" className="block">
                      <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg hover:from-purple-100 hover:to-indigo-100 dark:hover:from-purple-900/30 dark:hover:to-indigo-900/30 transition-all cursor-pointer">
                        <h4 className="font-medium text-emerald-800 dark:text-emerald-100 text-sm">{group.name}</h4>
                        <p className="text-xs text-emerald-600 dark:text-emerald-300 mt-1">{group.members.length} members</p>
                        <span className="inline-block mt-2 px-2 py-1 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 text-xs rounded-full">
                          {group.category}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <Link to="/groups" className="block">
                  <div className="text-center py-6 hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-800 dark:to-indigo-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <UsersRound className="w-8 h-8 text-indigo-600 dark:text-indigo-300" strokeWidth={2} />
                    </div>
                    <p className="text-sm text-emerald-600 dark:text-emerald-300">Join a support group!</p>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div className="mt-8">
          <div className="card animate-fade-in-up">
            <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/mood" className="group relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:scale-105 transition-all shadow-lg hover:shadow-xl" data-testid="quick-mood-btn">
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <Smile className="w-10 h-10 mb-3 mx-auto opacity-80" strokeWidth={2} />
                <span className="block text-sm font-semibold">Track Mood</span>
              </Link>
              
              <Link to="/matches" className="group relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-pink-500 to-rose-500 text-white hover:scale-105 transition-all shadow-lg hover:shadow-xl" data-testid="quick-matches-btn">
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <HeartHandshake className="w-10 h-10 mb-3 mx-auto opacity-80" strokeWidth={2} />
                <span className="block text-sm font-semibold">Find Matches</span>
              </Link>
              
              <Link to="/groups" className="group relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-blue-500 to-indigo-500 text-white hover:scale-105 transition-all shadow-lg hover:shadow-xl" data-testid="quick-groups-btn">
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <UsersRound className="w-10 h-10 mb-3 mx-auto opacity-80" strokeWidth={2} />
                <span className="block text-sm font-semibold">Join Groups</span>
              </Link>
              
              <Link to="/wellness" className="group relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-emerald-500 to-green-500 text-white hover:scale-105 transition-all shadow-lg hover:shadow-xl" data-testid="quick-wellness-btn">
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <Leaf className="w-10 h-10 mb-3 mx-auto opacity-80" strokeWidth={2} />
                <span className="block text-sm font-semibold">Wellness</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;